import { Menu, Select } from "antd";
import type { MenuProps } from "antd";
import { Link } from "react-router";
import SitesIcon from "@/assets/icons/sites.svg?react";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

type SiteOption = {
  value: string;
  label: string;
};

type SiteSidebarProps = {
  sitesList: SiteOption[];
  menuItems: MenuProps["items"];
  activeKey: string;
  currentSiteId: string;
  onSiteChange: (siteId: string) => void;
  onMenuSelect: (key: string) => void;
  portfolioSiteKey?: string | null;
};

export default function SiteSidebar({
  sitesList,
  menuItems,
  activeKey,
  currentSiteId,
  onSiteChange,
  onMenuSelect,
  portfolioSiteKey,
}: SiteSidebarProps) {
  const portfolioProfileHidden = useFeatureFlag(
    FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN,
  );

  return (
    <div className="ssj-sidebar w-[280px] min-w-[280px] sticky top-0 self-start h-screen flex flex-col gap-2 p-3 pb-6 overflow-y-auto">
      {/* Site selector card */}
      <div
        className="h-[54px] pl-3 pr-2 py-3 flex items-center gap-3"
        style={{
          borderRadius: 8,
          border: "1px solid rgba(0, 0, 0, 0.04)",
          background: "#FFF",
          boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
        }}
      >
        <div className="flex items-center p-[6px] rounded-[4px] bg-[#2B4752] text-white icon-small">
          <SitesIcon width={26} height={26} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-[#556C75] font-semibold tracking-wide leading-tight ml-[7px]">
            SITE
          </div>
          <Select
            value={currentSiteId}
            className="w-full"
            options={sitesList}
            onChange={onSiteChange}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            size="small"
            variant="borderless"
          />
        </div>
      </div>

      {/* Navigation menu card */}
      <div
        className="overflow-y-auto overflow-clip"
        style={{
          borderRadius: 8,
          border: "1px solid rgba(0, 0, 0, 0.04)",
          background: "#FFF",
          boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          items={menuItems}
          onClick={({ key }) => onMenuSelect(key)}
          style={{ borderRight: "none", background: "transparent" }}
        />
      </div>

      {portfolioSiteKey && !portfolioProfileHidden && (
        <Link
          to={`/portfolio/site-profiles/${portfolioSiteKey}`}
          className="flex items-center justify-between px-3 py-2 rounded-[6px] border border-[#E5EDF0] bg-white text-[12px] text-[#48636F] hover:text-[#1A6FAD] hover:border-[#1A6FAD] transition-colors no-underline"
        >
          <span>Full site profile</span>
          <span className="text-[10px] opacity-60">↗</span>
        </Link>
      )}
    </div>
  );
}
